import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import config from "../../config/config";
import styles from "./ManOfTheMatch.module.css";
import { ManOfTheMatchService } from "../../services/man_of_the_match_service";
import {
    FaTrophy,
    FaCalendarAlt,
    FaFutbol,
    FaUsers,
    FaArrowRight,
    FaSearch,
    FaTimes
} from "react-icons/fa";
import { useManOfTheMatchState } from "../../states/useManOfTheMatchState";

const Manofthematch = () => {
    const manOfTheMatchService = ManOfTheMatchService();
    const {
        awards,
        setAwards,
        filteredAwards,
        setFilteredAwards,
        loading,
        setLoading,
        searchTerm,
        setSearchTerm,
        activeFilter,
        setActiveFilter
    } = useManOfTheMatchState();

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchAwards();
    }, []);

    useEffect(() => {
        filterAwards();
    }, [awards, searchTerm, activeFilter]);

    const fetchAwards = async () => {
        try {
            await manOfTheMatchService.fetchManOfTheMatch((awardsData) => {
                // Sort by most recent match date first
                const sortedAwards = awardsData.sort((a, b) => {
                    // First sort by active status (active first)
                    if (a.is_active !== b.is_active) {
                        return b.is_active - a.is_active;
                    }
                    // Then sort by match date (newest first)
                    return new Date(b.match_date) - new Date(a.match_date);
                });
                setAwards(sortedAwards);
            });
        } catch (error) {
            console.error("Error fetching Man of the Match awards", error);
        } finally {
            setLoading(false);
        }
    };

    const filterAwards = () => {
        let filtered = awards;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(award =>
                award.player_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                award.team_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                award.match_title?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by active status
        if (activeFilter === "active") {
            filtered = filtered.filter(award => award.is_active);
        } else if (activeFilter === "inactive") {
            filtered = filtered.filter(award => !award.is_active);
        }

        setFilteredAwards(filtered);
    };

    const getFullImageUrl = (imagePath) => {
        if (!imagePath) return "/default-player.png";
        if (imagePath.startsWith("http")) return imagePath;
        return `${config.baseURL}${imagePath}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Clear search input
    const clearSearch = () => {
        setSearchTerm("");
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading Man of the Match Awards...</p>
            </div>
        );
    }

    const fadeUp = {
        hidden: {
            opacity: 0,
            y: 60,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: "easeOut",
            },
        },
    };

    const staggerChildren = {
        visible: {
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    return (
        <div className={styles.manOfTheMatchPage}>
            {/* Hero Section */}
            <section className={styles.heroSection}>
                <div className={styles.heroBackground}>
                    <div className={styles.heroOverlay}></div>
                </div>
                <div className={styles.heroContent}>
                    <div className={styles.container}>
                        <div className={styles.heroBadge}>Player Excellence</div>
                        <h1 className={styles.heroTitle}>
                            <span>Man of the Match</span>
                        </h1>
                        <p className={styles.heroSubtitle}>
                            Celebrating outstanding individual performances in Royal Premier League matches
                        </p>
                    </div>
                </div>
            </section>

            {/* Search Section - ALWAYS SHOW THIS */}
            <section className={styles.controlsSection}>
                <div className={styles.container}>
                    <div className={styles.controlsRow}>
                        <div className={styles.searchGroup}>
                            <div className={styles.searchBox}>
                                <div className={styles.searchIcon}>
                                    <FaSearch />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search by player, team, or match..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className={styles.searchInput}
                                    aria-label="Search awards"
                                />
                                {searchTerm && (
                                    <button
                                        className={styles.searchClear}
                                        onClick={clearSearch}
                                        aria-label="Clear search"
                                        type="button"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Awards Grid Section - Only show when there are awards */}
            {filteredAwards.length > 0 ? (
                <section className={`${styles.awardsSection} ${styles.section}`}>
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Award Winners</h2>
                            <div className={styles.awardsCount}>
                                Showing {filteredAwards.length} of {awards.length} awards
                            </div>
                        </div>

                        <motion.div
                            className={styles.awardsGrid}
                            variants={staggerChildren}
                            initial="hidden"
                            animate="visible"
                        >
                            {filteredAwards.map((award, index) => (
                                <motion.div
                                    key={award.id}
                                    className={`${styles.awardCard} ${!award.is_active ? styles.inactive : ""}`}
                                >
                                    <div className={styles.playerImageContainer}>
                                        {award.image ? (
                                            <img
                                                src={getFullImageUrl(award.image)}
                                                alt={award.player_name}
                                                className={styles.playerImage}
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                    e.target.nextSibling.style.display = "flex";
                                                }}
                                            />
                                        ) : null}
                                        {!award.image && (
                                            <div className={styles.defaultPlayerImage}>
                                                <FaFutbol />
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.awardContent}>
                                        <h3 className={styles.playerName}>{award.player_name}</h3>

                                        <div className={styles.matchInfo}>
                                            <div className={styles.matchTitle}>
                                                <FaUsers className={styles.teamIcon} /> Team:
                                                <span className={styles.textTruncate2Lines} style={{ marginLeft: '11px', textAlign: 'justify' }}>
                                                    {award.team_name ? award.team_name : "No detail found."}
                                                </span>
                                            </div>
                                            <div className={styles.matchTitle}>
                                                <FaFutbol className={styles.matchIcon} /> Match:
                                                <span className={styles.textTruncate2Lines} style={{ marginLeft: '5px', textAlign: 'justify' }}>
                                                    {award.match_title ? award.match_title : "No detail found."}
                                                </span>
                                            </div>

                                            <div className={styles.matchDate}>
                                                <FaCalendarAlt className={styles.dateIcon} /> Date:
                                                <span style={{ marginLeft: '15px' }}>{formatDate(award.match_date)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>
            ) : (
                /* No Awards Message - Show when there are NO awards */
                <div className={styles.noAwards}>
                    <div className={styles.noAwardsIcon}>
                        <FaTrophy />
                    </div>
                    <h3>No Awards Found</h3>
                    <p>
                        {searchTerm
                            ? "No Man of the Match awards match your search criteria"
                            : "No awards available at the moment"}
                    </p>
                    {searchTerm && (
                        <button
                            className={styles.clearSearch}
                            onClick={clearSearch}
                        >
                            Clear Search
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default Manofthematch;